import React, { useState, useEffect } from 'react'
import GoogleMapReact from 'google-map-react'
import Supercluster from 'supercluster'
import { useNavigate, useLocation } from 'react-router-dom'
import { getZoom, getCenter } from '../../js/mapHelpers'
import CourseMarker from './CourseMarker'
import ClusterMarker from './ClusterMarker'
import Popup from './Popup'

const Marker = ({ children }) => children

function GroupsMap(props) {
  const navigate = useNavigate()
  const location = useLocation()
  const propsZoom = props.zoom || 5.5

  const [groups, setGroups] = useState([])
  const [points, setPoints] = useState([])
  const [zoom, setZoom] = useState(propsZoom)
  const [bounds, setBounds] = useState(null)
  const [supercluster, setSupercluster] = useState(null)
  const [popup, setPopup] = useState(null)
  const [defaultZoom] = useState(getZoom(location, propsZoom))
  const [defaultCenter] = useState(
    getCenter(location, props.country, props.state)
  )

  const mapRef = React.createRef()

  const createSuperclusterState = (groups) => {
    console.log('createSuperclusterState', groups)
    const points = groups.map((group) => ({
      type: 'Feature',
      properties: { cluster: false, id: group.id, category: group.name },
      geometry: {
        type: 'Point',
        coordinates: [group.location.lng, group.location.lat],
      },
    }))

    const supercluster = new Supercluster({
      radius: 40,
      maxZoom: 20,
    })

    supercluster.load(points)

    return {
      groups,
      points,
      supercluster,
    }
  }

  useEffect(() => {
    const _state = createSuperclusterState(props.groups)

    setGroups(_state.groups)
    setPoints(_state.points)
    setSupercluster(_state.supercluster)
  }, [props.groups])

  const onCourseGroupClicked = (cluster) => {
    const { id } = cluster.properties
    props.onCourseGroupClicked(groups.find((group) => group.id === id))
  }

  const onClusterClicked = (cluster) => {
    const children = supercluster.getChildren(cluster.id)
    console.log('Click onClusterClicked', cluster, children)
    setPopup({
      cluster,
      children,
    })
  }

  const renderMarkers = () => {
    console.log('renderMarkers')
    if (!supercluster || !bounds || bounds.length === 0) return null

    try {
      const clusters = supercluster.getClusters(bounds, zoom)
      return clusters.map((cluster) => {
        const [longitude, latitude] = cluster.geometry.coordinates
        const { cluster: isCluster, point_count: pointCount } =
          cluster.properties

        if (isCluster) {
          return (
            <Marker
              key={`cluster-${cluster.id}`}
              lat={latitude}
              lng={longitude}
            >
              <ClusterMarker
                cluster={cluster}
                supercluster={supercluster}
                mapRef={mapRef}
                pointCount={pointCount}
                pointsLength={points.length}
                onClickCluster={(cluster) => onClusterClicked(cluster)}
              />
            </Marker>
          )
        }

        return (
          <Marker
            key={`course-${cluster.properties.id}`}
            lat={latitude}
            lng={longitude}
          >
            <CourseMarker
              cluster={cluster}
              onClick={() => onCourseGroupClicked(cluster)}
            />
          </Marker>
        )
      })
    } catch (e) {
      console.error(e, 'Failed to render markers')
      return null
    }
  }

  return (
    // Important! Always set the container height explicitly
    <div style={{ height: '50vh', width: '100%' }}>
      <GoogleMapReact
        bootstrapURLKeys={{ key: process.env.REACT_APP_MAPS_KEY }}
        defaultCenter={defaultCenter}
        defaultZoom={defaultZoom}
        center={getCenter(location, props.country, props.state)}
        zoom={getZoom(location, propsZoom)}
        yesIWantToUseGoogleMapApiInternals={true}
        onGoogleApiLoaded={({ map }) => {
          mapRef.current = map
        }}
        onChange={({ center, zoom, bounds }) => {
          console.log('onChange', center, zoom, bounds)
          const currentSearch = location.search
          const newSearch = `?zoom=${zoom}&center=${center.lat},${center.lng}`
          if (newSearch !== currentSearch) {
            navigate(location.pathname + newSearch)
          }
          setZoom(zoom)
          setBounds([
            bounds.nw.lng,
            bounds.se.lat,
            bounds.se.lng,
            bounds.nw.lat,
          ])
        }}
        options={{
          mapTypeControl: true,
        }}
      >
        {renderMarkers()}
        {popup && (
          <Marker
            key="popup"
            lat={popup.cluster.geometry.coordinates[1]}
            lng={popup.cluster.geometry.coordinates[0]}
          >
            <Popup
              clusters={popup.children}
              onClickCourse={onCourseGroupClicked}
            />
          </Marker>
        )}
      </GoogleMapReact>
    </div>
  )
}

export default GroupsMap
